import { isEqual, isNil, keys } from 'lodash';
import { Button } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../InteractionStep';
import { CardAlert } from '../../TempComponents';
import { useContext, useState, useMemo, memo } from 'react';
import {
  DEFAULT_NER_TITLE,
  DEFAULT_NER_SUBTITLE,
  InteractionStepType,
  NamedEntityRecognitionStepResponse,
  IndexedInteractionStepResponse,
} from '@scale/shared/types/chat';
import { NamedEntityRecognition } from 'react-scale/components/scalers/tasks/namedEntityRecognition/NamedEntityRecognition';
import { INamedEntityRecognitionResponse } from '@scale/shared/types/response/namedentityrecognition';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useTrans } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useTrans';
import {
  useChatStoreExtract,
  useMostRecentResponse,
  useStepParams,
  useStepResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { StepWidthWrapper, StepWidth } from '../../step/StepWidthWrapper';
import { CommentButton } from '../../feedback/CommentButton';
import { PinButton } from '../../step/PinButton';
import { StepCard } from '../../step/StepCard';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';

const NamedEntityRecognitionTaskComponent: InteractionStepTaskComponent = ({
  readOnly,
  responseIndex,
  isPinnedView,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );
  const markStepAsCompletedByIndex = useChatStoreExtract(state => state.markStepAsCompletedByIndex);
  const step = useStepParams<InteractionStepType.NER>(responseIndex);
  const response = useStepResponse<NamedEntityRecognitionStepResponse>(responseIndex);
  const responses = useChatStoreExtract(state => state.responses);
  const processSuperVisionStepParams = useStepParams<InteractionStepType.ProcessSupervision>(
    responseIndex - 1,
  );

  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });
  const { t } = useTrans();
  const [lastSavedResponse, setLastSavedResponse] = useState<INamedEntityRecognitionResponse>(
    response?.context?.response ?? {
      annotations: [],
      relationships: [],
      global_attributes: {},
    },
  );
  const [currentResponse, setCurrentResponse] =
    useState<INamedEntityRecognitionResponse>(lastSavedResponse);

  const lastMessage = useMostRecentResponse(responseIndex);

  const initialResponse = {
    ...lastSavedResponse,
    annotations: Array.isArray(lastSavedResponse.annotations) ? lastSavedResponse.annotations : [],
  };

  const resultChanged = useMemo(
    () => !isEqual(currentResponse, lastSavedResponse),
    [currentResponse, lastSavedResponse],
  );
  const providedText = useMemo(() => {
    let response: IndexedInteractionStepResponse | undefined = undefined;
    for (let i = responseIndex; i >= 0; i--) {
      if (responses[i]?.step_id === step.params.provided_text_step_id) {
        response = responses[i];
        break;
      }
    }
    switch (response?.type) {
      case InteractionStepType.TextCollection: {
        if (step.params.provided_text_field_id) {
          return (
            /**
             * There are cases when the text collection step has an empty response (i.e no annotations yet). This
             * can happen if both the NER step and the depended-on text collection step are initially visible in a
             * L0 or above review level that renders both steps at once. In that case, we want to show an empty string
             * as the provided text UNTIL the user has added annotations for the `provided_text_field_id` to the
             * text collection step.
             */
            (response.context.response.annotations?.[step.params.provided_text_field_id]
              ?.response[0] as string) ?? ''
          );
        } else {
          return lastMessage;
        }
      }
      case InteractionStepType.ModelResponseSelector: {
        if (processSuperVisionStepParams?.params?.output_first_bad_rating_only) {
          return lastMessage;
        }
        if (step.params.provided_model_id) {
          return (
            response.context.candidates.find(
              candidate => candidate.id === step.params.provided_model_id,
            )?.message.content ?? lastMessage
          );
        }
        return (
          response.context.candidates.find(
            candidate => candidate.id === response?.context.selectedId,
          )?.message.content ?? lastMessage
        );
      }
      case InteractionStepType.PromptInput:
      case InteractionStepType.MultiTurnContinue: {
        if (response.context.referenceTexts && response.context.referenceTexts.length > 0) {
          return (
            response.context.referenceTexts?.reduce((acc, ref) => {
              acc += '\n' + ref.content;
              return acc;
            }, '') ?? lastMessage
          );
        } else {
          return response.output;
        }
      }
      default:
        return lastMessage;
    }
  }, [
    responseIndex,
    responses,
    step.params.provided_text_step_id,
    step.params.provided_text_field_id,
    step.params.provided_model_id,
    lastMessage,
    processSuperVisionStepParams?.params?.output_first_bad_rating_only,
  ]);

  return (
    <CardAlert message={response && resultChanged ? 'Please save the section below.' : undefined}>
      <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
        <div className="flex flex-col gap-2">
          <StepCard readOnly={readOnly}>
            <StepCard.Header>
              <StepCard.HeaderLeft>
                <StepCard.Icon icon="question-circle" />
                <StepCard.HeaderColumn>
                  <StepCard.Title>{step.title || DEFAULT_NER_TITLE}</StepCard.Title>
                  <StepCard.Subtitle>{step.subtitle || DEFAULT_NER_SUBTITLE}</StepCard.Subtitle>
                </StepCard.HeaderColumn>
              </StepCard.HeaderLeft>
              <StepCard.HeaderRight>
                {!taskContext.enableUnifiedFeedback && (
                  <CommentButton
                    visible={true}
                    childId={constructStepChildId(step)}
                    turnIndex={step.turn}
                  />
                )}
                <PinButton responseIndex={responseIndex} />
              </StepCard.HeaderRight>
            </StepCard.Header>
            <StepCard.Body className="flex justify-center p-0">
              <NamedEntityRecognition
                initialResponse={initialResponse}
                labels={step.params.labels}
                relationships={step.params.relationships}
                defaultAttributeFields={step.params.default_attribute_fields}
                text={providedText}
                allowOverlappingAnnotations={step.params.allow_overlapping_annotations}
                readOnly={!!readOnly}
                onResponseChange={newResponse => {
                  setCurrentResponse(newResponse);
                  const matchesLastSavedResponse = isEqual(newResponse, lastSavedResponse);
                  const canUpdateCompletedState = response && !isNil(responseIndex);
                  if (canUpdateCompletedState && !matchesLastSavedResponse) {
                    markStepAsIncompletedByIndex(responseIndex);
                  } else if (canUpdateCompletedState && matchesLastSavedResponse) {
                    markStepAsCompletedByIndex(responseIndex);
                  }
                }}
                hideLabels={true}
              />
            </StepCard.Body>
            {!taskContext.previewMode && (
              <StepCard.Footer className="flex justify-end">
                <Button
                  onClick={async () => {
                    if (!onSubmit) {
                      return;
                    }
                    const submitPassed = await onSubmit(
                      {
                        type: InteractionStepType.NER,
                        context: {
                          response: currentResponse,
                        },
                        output: lastMessage,
                      },
                      responseIndex,
                      step,
                    );
                    if (submitPassed) {
                      setLastSavedResponse(currentResponse);
                    }
                  }}
                  fullWidth
                  disabled={readOnly || (!resultChanged && providedText.length > 0)}
                >
                  {t('save-and-continue')}
                </Button>
              </StepCard.Footer>
            )}
          </StepCard>
          {response && response.context.response?.annotations?.length > 0 && (
            <StepCard readOnly={readOnly}>
              <StepCard.Header>
                <StepCard.Subtitle>Annotation Summary</StepCard.Subtitle>
              </StepCard.Header>
              <StepCard.Body className="flex flex-wrap gap-2">
                {response.context.response.annotations.map((annotation, index) => (
                  <div key={index} className="bg-neutral-200 p-2 rounded-md text-neutral-700">
                    <p className="text-sm font-bold ">{annotation.label}</p>
                    <p className="text-xs ">{annotation.text}</p>
                    <div>
                      {keys(annotation.attributes).map(key => {
                        const value = annotation.attributes![key];
                        return <div key={key}>{'text' in value ? value.text : value.selected}</div>;
                      })}
                    </div>
                  </div>
                ))}
              </StepCard.Body>
            </StepCard>
          )}
        </div>
      </StepWidthWrapper>
    </CardAlert>
  );
};

export default memo(NamedEntityRecognitionTaskComponent);
